/**
 * The crafted-session page ↔ ActivityHost seam.
 *
 * Authored activities emit over their own HTTP channel, not through the
 * page's dispatch handlers — but the page still needs to know when an
 * emission advanced the session (a unit's last span closed, the
 * route completed) so it can refresh its snapshot and show the wrap.
 * The registry's renderer props are shared by every interaction kind,
 * so this rides a context instead of a new prop.
 *
 * It also carries the coach door for an activity's OWN glosses.  A
 * learner can already highlight any text inside a mounted activity and
 * dive from the selection popover; what that cannot do is let an AUTHOR
 * mark the words worth tapping and hand the coach the right one.  An
 * activity raises `DIVE_DEEPER_EVENT` on its own DOM and the host
 * forwards it — a bubbling event rather than a handle on `Ctx`, for two
 * reasons:
 *
 *   · a coach request is not evidence, so it has no business on the
 *     five-verb `EngineChannel`, whose every verb is priced, recorded
 *     and replayed in golden traces;
 *   · `packages/session-api/tests/hostedChrome.test.ts` makes `ctx.mode`
 *     the ONE tell that a host is driving, explicitly so an activity
 *     cannot detect a host some other way.  An activity cannot observe
 *     whether anything listens to a DOM event, so this adds no tell.
 *
 * The activity mounts into the host's own DOM (there is no iframe — it
 * is why the selection popover can read its text at all), so the event
 * simply bubbles.
 */

import { createContext } from "react";

import type { EmissionAck } from "@sharpana/session-api/types";

export interface CraftedSessionContextValue {
  onEmissionAck: (ack: EmissionAck, emission: Record<string, unknown>) => void;
  /** Open a nested deep-dive scope on a term an activity glossed itself. */
  onDiveDeeper: (term: string) => void;
}

/**
 * The event an authored activity raises to open a deep dive on one of
 * its own glossed terms.  `detail.term` is the word as the learner read
 * it.  Bubbling and composed, so it crosses the activity's React root
 * and any shadow boundary to reach the host's viewport element.
 *
 * Vessels write this string literally — a published bundle is immutable
 * and cannot import from the app — so it is API: renaming it orphans
 * every activity already on the shelf.
 */
export const DIVE_DEEPER_EVENT = "coaching:dive-deeper";

export interface DiveDeeperEventDetail {
  term: string;
}

export const CraftedSessionContext = createContext<CraftedSessionContextValue | null>(null);
